type MediaPayloadClickEvent = {
    target: EventTarget | null
    currentTarget: EventTarget | null
    preventDefault?: () => void
    stopPropagation: () => void
}

type MediaPayloadClickOptions = {
    preventDefault?: boolean
}

// Chrome that lies on top of media is not itself media: the transport strip on a combined
// image+audio card covers the artwork's bottom edge, but its inert area — the gaps between the
// play button, the seek bar and the menu — is still part of the post. A click there means
// "open this", the same as a click on the answer's text.
//
// That click has to survive every stopPropagation between the strip and the answer's openRight
// handler, and there are four: the audio player root, FileEmbed's audio wrapper, FileEmbed's
// outer file wrapper, and this module. The player layers take an explicit prop and simply stop
// capturing; this one is reached by clicks that started anywhere in the payload, so it needs
// to recognize the region on sight. Marking the region in the DOM is what lets it.
//
// Only inert area arrives here — every audio control stops the event in its own handler before
// the strip ever sees it — so the region can pass through wholesale without probing the target.
export const MEDIA_PAYLOAD_PASSTHROUGH_ATTRIBUTE = 'data-media-payload-passthrough'

const PASSTHROUGH_SELECTOR = `[${MEDIA_PAYLOAD_PASSTHROUGH_ATTRIBUTE}]`

export function isMediaPayloadPassthroughTarget(target: EventTarget | null): boolean {
    const element = target as Element | null
    if (!element || typeof (element as Element).closest !== 'function') return false
    return Boolean(element.closest(PASSTHROUGH_SELECTOR))
}

export function suppressMediaPayloadContentClick(
    event: MediaPayloadClickEvent,
    { preventDefault = false }: MediaPayloadClickOptions = {},
) {
    // Mobile media indents are padding on the payload wrapper. A direct wrapper click is
    // therefore outside the media and should keep bubbling to the answer's openRight handler.
    if (event.target === event.currentTarget) return

    // Media chrome that opted out of capturing keeps bubbling for the same reason.
    if (isMediaPayloadPassthroughTarget(event.target)) return

    if (preventDefault) event.preventDefault?.()
    event.stopPropagation()
}
